import { useCallback, useEffect, useRef, useState } from 'react';

import type { AdItem, BloomRuntimeConfig, ChannelSummary } from '@apm/contracts';
import { LocalStorageAdapter, resolveDeviceId, resolvePlayerKind } from '@apm/bloom-receiver-sdk';

import { AdPurchaseButton, AdSkipButton } from '../components/AdControls.js';
import { AppHeader } from '../components/AppHeader.js';
import { HandoffErrorOverlay } from '../components/HandoffErrorOverlay.js';
import { AdCountdown } from '../components/PlaybackClock.js';
import { ProductSheet } from '../components/ProductSheet.js';
import { PushSentOverlay } from '../components/PushSentOverlay.js';
import { QrOverlay } from '../components/QrOverlay.js';
import { ChannelSheet } from '../components/ChannelSheet.js';
import { FreezeFrame } from '../components/FreezeFrame.js';
import { MuteButton } from '../components/MuteButton.js';
import { PlayerIcon } from '../components/icons/PlayerIcons.js';
import { SettingsSheet } from '../components/SettingsSheet.js';
import { StartGate } from '../components/StartGate.js';
import type { EpgRow } from '../epg.js';
import { useT } from '../i18n/LocaleProvider.js';
import { showsStartGate } from '../auto-start.js';
import { channelForNumber } from '../channel-keys.js';
import { closesSwitchEpg, neighborChannel, watchingChannelId } from '../epg.js';
import { buffersUnlocked, unlockBuffers } from '../playback-buffers.js';
import { useChannelNumberKeys } from '../use-channel-keys.js';
import { useOnairPlayer } from '../onair/use-onair-player.js';
import { breakElapsedSec, cspmSkipButton, onairAdInsertionFor } from '../onair/cspm.js';
import { runAdHandoff } from '../ad-handoff.js';
import { resolveClientType } from '../client-type.js';
import { config } from '../config.js';
import { HANDOFF_IDLE, handoffLingerMs, sheetLinkContext } from '../handoff.js';
import type { HandoffState } from '../handoff.js';
import { healAfterClose } from '../tv-focus/use-tv-focus.js';
import { useAudioMuted } from '../audio-muted.js';
import type { AuthApi } from '../auth/use-auth.js';
import type { Tv2mApi } from '../useTv2m.js';

/**
 * ONAIR 채널의 시청 화면 (v28, `docs/57` §1.3·§8 · 절대 규칙 74~76).
 *
 * ── ★ 없는 것으로 정의된다 ──────────────────────────────────
 *
 * | 개인화 채널 | ONAIR |
 * |---|---|
 * | `[건너뛰기]` | **없다** |
 * | `[전체 회차]` | **없다** |
 * | 이어보기 | **없다 — 언제나 라이브** |
 * | 구매·상품 오버레이 | **없다** (v29 범위) |
 * | 광고 | **없다 — CSPM 채널만 있다** (v38 · 아래) |
 * | 진행 표시 `00:42 / 05:01` | **없다 — 「방송 중」** |
 * | 음소거·설정·채널·편성표·▲▼·숫자 키 | **그대로 있다** |
 *
 * ── 왜 `WatchScreen` 에 분기를 넣지 않았는가 ─────────────────
 *
 * 저 화면은 1,700줄이고 **리시버가 있다는 전제가 깊다**(`view.started`·`getPlayhead`·
 * 광고·상품·핸드오프·진행 보고). 분기를 넣으면 그 전제들이 「있을 수도 없을 수도」가 되어
 * **없는 쪽의 조합이 영원히 안 밟히는 코드**가 된다(`docs/57` D9).
 *
 * ── 이벤트를 하나도 보내지 않는다 ───────────────────────────
 *
 * `EVENT_TYPES` 여섯 중 어느 것도. 받은 `playlistId` 가 없기 때문이고(플레이리스트를
 * 받은 적이 없다), **구형 FAST 채널에 시청 데이터가 없다는 것이 대조군의 진실**이라
 * 흉내 내지 않고 그냥 그렇게 둔다 (절대 규칙 76).
 *
 * ── v38 · CSPM 채널의 광고 화면 (`docs/77` §5.5 · 절대 규칙 101) ─────
 *
 * 서버가 깐 슬레이트를 단말의 Flower 가 광고로 바꾸고, **화면은 개인화 채널의 광고와 같다** — 남은 시간 · 광고 구매 · 스킵.
 * 부품은 `AdControls`·`runAdHandoff`·`AdCountdown` 을 `WatchScreen` 과 같이 쓴다(두 벌이면 한쪽만 고쳐진다). 근거는
 * **Flower 의 리스너와 `AdInfo.vastAdXml` 하나**이고(`onair/cspm.ts`) 리시버는 여전히 없다. BLOOM `Event`·진행 보고·회차의 구매는
 * 여전히 0 이다 — 생기는 것은 시청자가 누른 광고 구매(TV2M `ad`)와 Flower 가 광고 서버로 보내는 트래킹뿐이다.
 */
export function OnairScreen({
  channel,
  autoStart,
  runtime,
  tv2m,
  auth,
  epgRows,
  onEpgOpen,
  favorites,
  onToggleFavorite,
  onSwitchChannel,
  epgOpen = false,
  onHome,
}: {
  channel: ChannelSummary;
  autoStart: boolean;
  runtime: BloomRuntimeConfig;
  tv2m: Tv2mApi;
  auth: AuthApi;
  epgRows: readonly EpgRow[];
  onEpgOpen: () => void;
  favorites: readonly string[];
  onToggleFavorite: (channelId: string) => void;
  onSwitchChannel: (channelId: string, fromEpg?: boolean) => void;
  /**
   * v29. **편성표에서 고른 전환으로 들어왔다** — 시트를 연 채로 시작한다
   * (2026-09-06 · `closesSwitchEpg`). 한 번 쓰고 버린다.
   */
  epgOpen?: boolean;
  /** v32 — 「홈」 버튼. 진행 보고는 없다(절대 규칙 76) */
  onHome: () => void;
}) {
  const t = useT();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  // v29. **전환을 건너 열린 채로 온다** (2026-09-06). 마운트에서 한 번 읽고 버린다.
  const [channelSheetOpen, setChannelSheetOpen] = useState(epgOpen);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [muted] = useAudioMuted();
  /*
   * v38 — **켜는 판정은 `onairAdInsertionFor()` 하나다**(규칙 101). 마운트에서 한 번 — 채널이 바뀌면 이 화면이 `key` 로 새로
   * 선다. 기기 id 는 리시버가 쓰는 그 값(`fast.deviceId`)이다 — Flower 의 `setDeviceId`·태그의 `dev`·핸드오프의
   * `receiverDeviceId` 가 같아야 광고 서버의 트래킹과 TV2M 의 발송이 같은 기기를 가리킨다.
   */
  const [insertion] = useState(() => onairAdInsertionFor(channel, resolvePlayerKind(), window.location.search));
  const [deviceId] = useState(() => resolveDeviceId('', new LocalStorageAdapter()));
  const [clientType] = useState(() => resolveClientType());
  const breakSec = insertion.mode === 'cspm' ? insertion.breakSec : 0;

  const { view, play, ads, cspmStatus, flowerErrors, skipAd, getMediaSec, getLatency, getPlayingLagSec } = useOnairPlayer({
    // **앵커도 주소도 없으면 `null` 이다** — 「아직 방송 전」이고 고장이 아니다.
    streamUrl: channel.onair?.streamUrl ?? null,
    container,
    autoStart,
    initialMuted: muted,
    cspm:
      insertion.mode === 'cspm'
        ? { adTagUrl: insertion.adTagUrl, channelId: channel.channelId, deviceId }
        : null,
  });

  /*
   * v38 — 스킵 버튼의 남은 초는 재생 위치로 센다(`cspmSkipButton`). 무장을 기다리는 동안만 두드린다 — 그 밖에는 리스너가
   * 화면을 바꾼다(`onAdSkippable`·`onAdSkipExpired`·`onAdPlay`).
   */
  const [mediaSec, setMediaSec] = useState(0);
  const skipWaiting = ads.current?.skip === 'waiting';
  useEffect(() => {
    if (!skipWaiting) return;
    const tick = () => {
      setMediaSec(getMediaSec());
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => {
      window.clearInterval(timer);
    };
  }, [skipWaiting, getMediaSec]);
  const skip = cspmSkipButton(ads, skipWaiting ? mediaSec : getMediaSec());
  const breakStart = ads.breakStartMediaSec;

  /*
   * v38 — 광고 시계의 축. 한가운데 합류면 Flower 가 알린 남은 길이로 **합류 전에 지나간 몫**을 더한다(`breakElapsedSec` · bloom.24 ·
   * `docs/74` §4F · 2026-10-28 운영 7번: 시작 뒤 10.3초에 합류한 창의 시계가 1:00 부터 세어 광고가 0:11 을 남기고 끝났다).
   */
  const getAdBreakElapsedSec = useCallback(
    () => breakElapsedSec(ads, breakSec, getMediaSec()),
    [ads, breakSec, getMediaSec],
  );

  /*
   * v38 — **광고 구매** (`WatchScreen.requestAdHandoff` 와 같은 문 · `runAdHandoff`). 누른 순간의 광고를 붙든다(v21/F13).
   * ONAIR 에는 회차도 플레이리스트도 없다 — `contentProductId`·`playlistId` 가 `null` 이다.
   */
  const [handoff, setHandoff] = useState<HandoffState>(HANDOFF_IDLE);
  const pressedAd = useRef<AdItem | null>(null);
  const adItem = ads.current?.item ?? null;
  const requestAdHandoff = useCallback(() => {
    if (adItem === null || handoff.kind === 'loading') return;
    pressedAd.current = adItem;
    setHandoff({ kind: 'loading' });
    void runAdHandoff({
      runtime,
      clientType,
      ad: adItem,
      contentProductId: null,
      channelId: channel.channelId,
      playlistId: null,
      receiverDeviceId: deviceId,
      senderDeviceId: tv2m.init?.deviceId ?? null,
      token: auth.token,
    }).then((next) => {
      setHandoff((previous) => (previous.kind === 'loading' ? next : previous));
    });
  }, [adItem, handoff.kind, runtime, clientType, channel.channelId, deviceId, tv2m.init, auth.token]);

  // 「알림을 보냈습니다」는 스스로 물러난다 — `WatchScreen` 의 그 효과와 같은 판정(`handoffLingerMs`)
  useEffect(() => {
    const delay = handoffLingerMs(handoff, Date.now());
    if (delay === null) return;
    const timer = window.setTimeout(() => {
      setHandoff((previous) => (previous === handoff ? HANDOFF_IDLE : previous));
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [handoff]);

  /*
   * v38 — **브레이크가 끝나면 시트 밖의 오버레이를 걷는다.** 개인화 채널은 「상품이 바뀌면」 걷는데 ONAIR 에는 그 순간이
   * 없다 — 남기면 화면에 없는 광고의 QR 을 스캔하게 된다. **시트는 남긴다**(시청자가 체크아웃 중일 수 있다 · `WatchScreen`
   * 의 `healAfterClose` ★ 와 같은 이유). 그리고 컨트롤 바의 구성이 갈리므로 포커스를 치유한다(규칙 32).
   */
  const inBreak = ads.inBreak;
  useEffect(() => {
    if (!inBreak) setHandoff((previous) => (previous.kind === 'sheet' ? previous : HANDOFF_IDLE));
    healAfterClose();
  }, [inBreak]);
  const showAdPurchase = adItem !== null;
  useEffect(() => {
    healAfterClose();
  }, [showAdPurchase, skip.visible]);

  /*
   * v38 — 진단 줄의 「지연」(`?debug=1` 에서만): 지금 프레임의 PDT 가 벽시계에서 몇 초 뒤인가. 두 창의 시차를 사람이 바로 읽게 둔다
   * (CS10 실기에서 「두 창이 10초 넘게 갈린다」를 가르려고). 7번의 스킵 뒤에는 PDT 가 본 광고만큼 밀려 커 보인다(`docs/77` §9).
   */
  const [lagSec, setLagSec] = useState<number | null>(null);
  useEffect(() => {
    if (!config.debug) return;
    const timer = window.setInterval(() => {
      setLagSec(getPlayingLagSec());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [getPlayingLagSec]);

  // v38 — 디버그 통로(`?debug=1` 에서만 · 하네스가 읽는다)
  useEffect(() => {
    if (!config.debug) return;
    window.__bloomOnair = { insertion, cspmStatus, ads, flowerErrors, skipAd, getMediaSec, getLatency, getPlayingLagSec };
    return () => {
      delete window.__bloomOnair;
    };
  }, [insertion, cspmStatus, ads, flowerErrors, skipAd, getMediaSec, getLatency, getPlayingLagSec]);

  const pending = channel.onair === null;

  /**
   * 옆 채널로 옮긴다 (절대 규칙 71). **▲▼ 와 숫자 키가 같은 함수를 탄다.**
   *
   * ── 개인화 화면의 `zapTo` 와 갈리는 것 둘 ────────────────────
   *
   * ① **진행 보고가 없다** — ONAIR 채널에는 지점이라는 것이 없다(절대 규칙 76).
   *    저쪽의 `reportEvent('channel_switch')` 자리가 여기서는 **비어 있는 것이 옳다.**
   * ② **`unlockBuffers()` 는 그대로 필요하다** — 게이트에서 숫자 키를 누르면 실제
   *    `play()` 는 새 화면의 `useEffect` 안이라 **제스처 밖**이고, 그때 증상은
   *    「눌렀는데 아무 일도 안 일어난다」 하나다(v26 이 값을 치른 자리).
   */
  const zapTo = useCallback(
    (next: string) => {
      if (next === channel.channelId) return;
      unlockBuffers();
      onSwitchChannel(next);
    },
    [channel.channelId, onSwitchChannel],
  );

  /*
   * ★ v29. **편성표에서 고른 전환은 새 채널이 나올 때 걷힌다** (2026-09-06).
   *
   * 개인화 화면과 **재료 하나가 다르다** — 저쪽은 `view.state === 'playing'` 이고
   * 여기는 **첫 프레임**(`view.playing`)이다. ONAIR 에는 리시버가 없어 그 상태가
   * 아예 없고, 마침 이쪽이 더 정확한 사실이다.
   */
  const openedBySwitch = useRef(epgOpen);
  useEffect(() => {
    if (!openedBySwitch.current || !channelSheetOpen) return;
    if (
      closesSwitchEpg({
        showing: view.playing,
        // ONAIR 화면은 잡은 프레임의 걷힘을 따로 듣지 않는다 — `playing` 이 곧 그 순간이다.
        frozen: false,
        failed: view.error !== null,
      })
    ) {
      openedBySwitch.current = false;
      setChannelSheetOpen(false);
    }
  }, [channelSheetOpen, view.playing, view.error]);

  const zap = useCallback(
    (step: 1 | -1) => {
      const next = neighborChannel(epgRows, channel.channelId, step);
      if (next === null) return;
      zapTo(next);
    },
    [epgRows, channel.channelId, zapTo],
  );

  // v26 — 숫자 키 (절대 규칙 71). **기본 층에서만 듣는다** — 훅이 그 판정을 갖는다.
  useChannelNumberKeys(
    useCallback(
      (number: number) => {
        const next = channelForNumber(epgRows, number);
        if (next === null) return;
        zapTo(next);
      },
      [epgRows, zapTo],
    ),
  );

  const mini = handoff.kind === 'sheet';

  return (
    <div className={`stage${mini ? ' stage--mini' : ''}`}>
      <div className="stage__video" ref={setContainer} />
      {/* v28. 개인화 채널과 **같은 것을 쓴다** — 전환의 빈 창은 양쪽에 똑같이 있다 */}
      <FreezeFrame />

      {/*
        ★ **`PlaybackClock` 을 쓰지 않는다.** 저것은 「남음 3:21 / 5:01」이고, ONAIR 에는
        남은 시간이라는 것이 없다(끝이 없다) — 그 자리에 **「방송 중」 하나**를 둔다.
        진행 표시를 그리려면 근거 데이터부터 다시 만들어야 하고, **근거 없는 표시는 틀린
        것보다 나쁘다**(절대 규칙 46 의 그 문장).
      */}
      <div className="stage__topleft">
        {/*
          ★ v29. **`started` 가 아니라 `playing` 이다** (2026-09-06). 저 값은 `play()` 가
          풀리기 **전에** 참이 되므로(게이트 지연을 없애려고 일부러 앞으로 당긴 자리다),
          그것으로 그리면 **검은 화면 위에 「방송 중」**이 뜬다 — 느린 망에서 실제로
          그랬고, 게이트를 걷어낸 뒤에는 더 자주 보인다.
          **근거 없는 표시는 틀린 것보다 나쁘다**(절대 규칙 46).
        */}
        {/*
          v38 — **브레이크 동안에는 광고 시계만** 그린다(개인화 채널이 광고 중에 회차 시계를 광고 시계로 갈아 끼우는 것과 같은 모양 ·
          CS10 실기에서 사용자가 두 화면을 맞대 지적했다).
        */}
        {view.playing && !ads.inBreak && (
          <span className="clock" data-testid="onair-live">
            {t('onair.live')}
          </span>
        )}
        {/*
          v38 — CSPM 브레이크의 남은 시간. 개인화 채널의 광고 시계와 같은 부품·같은 문구(`watch.adRemaining`)이고, 길이는 서버가
          깐 브레이크(`breakSec`)다 — Flower 의 선형 경로는 `onPrepare` 를 부르지 않고 앱은 `CUE-OUT` 을 읽지 않는다. 브레이크 한가운데에
          합류했으면 Flower 가 알린 남은 길이로 지난 초부터 센다(bloom.24 · `breakElapsedSec`).
        */}
        {ads.inBreak && breakStart !== null && (
          // `display: contents` — 시계(`<p>`)를 감싸되 좌상단 줄의 배치는 개인화 채널과 같게
          <div data-testid="onair-ad-clock" style={{ display: 'contents' }}>
            <AdCountdown
              durationSec={breakSec}
              getElapsedSec={getAdBreakElapsedSec}
            />
          </div>
        )}
        <MuteButton />
      </div>

      {pending && (
        <div className="notice">
          <p className="notice__message">{t('onair.pending')}</p>
        </div>
      )}

      {view.error !== null && !pending && (
        <div className="notice notice--error" data-focus-scope>
          <p className="notice__message">{t('onair.playbackFailed')}</p>
          <p className="notice__code">{view.error}</p>
          <button type="button" className="gate__button" onClick={play}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {/*
        ★★ **전환에서는 원리적으로 안 뜬다** (2026-09-06 · `showsStartGate`).
        여기가 고치기 전 **102~131ms** 로 가장 길게 보이던 자리다 — 개인화 채널보다
        오래 걸리는 이유는 매니페스트를 받고 첫 프레임이 붙기까지가 더 길기 때문이고,
        그래서 **조건을 바꾸는 것이 값을 줄이는 것보다 근본이다.**
      */}
      {showsStartGate({
        started: view.started,
        unlocked: buffersUnlocked(),
        autoStart,
        gestureRequired: view.gestureRequired,
      }) &&
        !pending &&
        view.error === null && (
        <StartGate
          onStart={play}
          onOpenGuide={
            epgRows.length > 0
              ? () => {
                  // **여는 순간 다시 받는다** (v27/TG15 · 절대 규칙 73 의 ★)
                  onEpgOpen();
                  setChannelSheetOpen(true);
                }
              : null
          }
        />
      )}

      {/*
        컨트롤 바. **`ControlBar` 를 재사용하지 않는다** — 저것은 리시버의 상태
        (스킵·광고·핸드오프·구매)를 그리는 컴포넌트이고, ONAIR 에는 그 넷이 다 없다.
        여기 있는 것은 **채널과 편성표 둘**뿐이다.

        네이티브 `<button>` 이다 — 포커스 후보가 `button:not([disabled]), a[href]` 뿐이라
        (절대 규칙 29·65) 그래야 리모컨이 닿는다.
      */}
      {handoff.kind === 'sheet' && (
        <ProductSheet
          // 이 화면의 시트는 언제나 광고다(`sheetForAd`) — 콘텐츠 자리는 없다
          context={sheetLinkContext(handoff, {
            productId: pressedAd.current?.creativeId ?? '',
            campaignId: pressedAd.current?.creativeId ?? '',
          })}
          note={handoff.note}
          checkout={tv2m}
          tv2mServerUrl={runtime.tv2mServerUrl}
          tracking={{ deviceId, playlistId: null }}
          onClose={() => {
            setHandoff(HANDOFF_IDLE);
          }}
        />
      )}
      {handoff.kind === 'push' && (
        <PushSentOverlay
          count={handoff.count}
          {...(handoff.source ? { source: handoff.source } : {})}
          {...(handoff.qrUrl ? { qrUrl: handoff.qrUrl } : {})}
        />
      )}
      {handoff.kind === 'qr' && <QrOverlay url={handoff.url} />}
      {handoff.kind === 'error' && (
        <HandoffErrorOverlay
          reason={handoff.reason}
          // **폴백 QR 이 없다** — 추적 4파라미터의 `pl` 이 ONAIR 에는 없다(규칙 8 · `WatchScreen` 도 `playlistId` 가 없으면 `null`)
          fallbackUrl={null}
        />
      )}

      {config.debug && (
        <div className="debug-onair" data-i18n="ko" data-testid="onair-cspm-debug">
          {insertion.mode === 'cspm' && (
            <>
              CSPM {cspmStatus?.state ?? '-'}
              {cspmStatus?.state === 'fallback' ? ` · ${cspmStatus.reason}` : ''} · 광고 {String(ads.counts.ads)} · 필러{' '}
              {String(ads.counts.fillers)} · 매칭 실패 {String(ads.counts.unmatched)}
              {ads.lastSkippedReason === null ? '' : ` · 건너뜀(${String(ads.lastSkippedReason)})`}
              {flowerErrors.length > 0 ? ` · 오류 ${String(flowerErrors.length)}` : ''}
              {' · '}
            </>
          )}
          지연 {lagSec === null ? '-' : lagSec.toFixed(1)}초
        </div>
      )}

      <div className="controls">
        {/*
          ★ **`ControlBar` 와 같은 모양이어야 한다** — 아이콘 + 번호, 이름은 `aria-label`·이름표
          (`docs/컨트롤바-아이콘-착수지시서.md`). **이름도 글자까지 같다**(2026-09-05, `verify-epg` 의
          `zapRingReturnsHome` 이 잡았다) — 그 하네스가 채널을 이 이름(`「번호 이름」`)으로 읽는다.
          처음에는 번호와 이름 사이에 공백이 없어 **「6Charade LIVE」**가 됐었다.

          `data-i18n="data"` 도 저쪽과 같은 자리다 — 등록된 값이라 한글 훑기의 대상이 아니다.
        */}
        <button
          type="button"
          className="controls__button controls__button--icon controls__button--channel"
          data-testid="channel-switch"
          data-i18n="data"
          aria-label={`${String(channel.number)} ${channel.name}`}
          onClick={() => {
            onEpgOpen();
            setChannelSheetOpen(true);
          }}
        >
          <PlayerIcon name="channel" />
          <span className="controls__number">{String(channel.number)}</span>
        </button>
        {/* v32 — 「홈」. 개인화 화면과 같은 자리·같은 뜻(`ControlBar` 의 그 주석). 진행 보고는 없다(규칙 76) */}
        <button
          type="button"
          className="controls__button controls__button--icon"
          data-testid="go-home"
          data-focus-back
          aria-label={t('watch.home')}
          onClick={onHome}
        >
          <PlayerIcon name="home" />
        </button>
        {/*
          ★ **▲▼ 는 개인화 채널과 같은 자리·같은 함수다** (절대 규칙 71).
          빠뜨렸더니 `verify-epg` 의 `zapsAllChannels` 가 **모든 채널을 한 번씩 밟다가**
          ONAIR 채널에서 멈췄다(2026-09-05) — 그 검사가 「어느 채널만 안 뜬다」를 잡는
          유일한 자리이고, 이 화면이 그 고리를 끊고 있었다.
        */}
        {epgRows.length > 1 && (
          <span className="controls__zap">
            <button
              type="button"
              className="controls__button controls__button--icon controls__button--zap"
              data-testid="channel-up"
              aria-label={t('watch.channelUp')}
              onClick={() => {
                zap(-1);
              }}
            >
              <PlayerIcon name="chUp" />
            </button>
            <button
              type="button"
              className="controls__button controls__button--icon controls__button--zap"
              data-testid="channel-down"
              aria-label={t('watch.channelDown')}
              onClick={() => {
                zap(1);
              }}
            >
              <PlayerIcon name="chDown" />
            </button>
          </span>
        )}
        {/*
          v38 — 광고 중의 버튼 둘(개인화 채널의 `ControlBar` 와 같은 부품 · `AdControls`). 광고 구매는 **원문을 읽은 광고에만**
          그린다 — 필러와 매칭 실패에는 근거가 없다(규칙 54). 스킵은 Flower 의 무장(`onAdSkippable`)으로 보이고 누르면
          `skipCurrentAd()` 다 — 남은 시간을 다시 묻는 것은 Flower 다.

          ★ **채널·홈·▲▼ 뒤에 둔다**(`ControlBar` 의 순서 그대로). 스킵 버튼은 `margin-left: auto` 로 줄 끝에 붙으므로 그 **뒤에 오는 것은
          전부 오른쪽으로 밀린다** — 처음 판은 이 둘을 줄 맨 앞에 두어 채널·홈·▲▼·설정이 오른쪽에 몰렸다(CS10 실기).
        */}
        {showAdPurchase && (
          <AdPurchaseButton
            handoffOpen={handoff.kind !== 'idle' && handoff.kind !== 'loading'}
            handoffPending={handoff.kind === 'loading'}
            onClick={requestAdHandoff}
          />
        )}
        <AdSkipButton
          skip={skip}
          onClick={() => {
            skipAd();
          }}
        />
      </div>

      {/*
        v38 — **설정은 오른쪽 위다**(개인화 채널의 `stage__settings` 와 같은 자리·같은 이름 · `data-testid="settings"`). v28 부터 컨트롤
        바 끝에 있었는데, 광고 중의 화면을 개인화 채널과 맞추면서 옮겼다 — 광고 밖에서도 같은 자리여야 버튼이 들락거리지 않는다.
      */}
      <button
        type="button"
        className="stage__settings"
        aria-label={t('watch.settings')}
        data-testid="settings"
        onClick={() => {
          setSettingsOpen((open) => !open);
        }}
      >
        {t('watch.settings')}
      </button>

      {channelSheetOpen && (
        <ChannelSheet
          rows={epgRows}
          // ★ 같은 자리다 — ONAIR 화면에도 진입 게이트가 있다 (2026-09-06).
          currentChannelId={watchingChannelId({
            channelId: channel.channelId,
            started: view.started,
          })}
          /*
           * **「지금 보는 항목」이 없다.** ONAIR 에는 선택이 없어 `resolveEpgPick` 의
           * `same`·`select` 갈래로 갈 일이 없고, 이 행에서 누를 수 있는 것은 **다른
           * 채널**뿐이다.
           */
          currentProductId={null}
          favorites={favorites}
          onToggleFavorite={onToggleFavorite}
          signedIn={auth.token !== null}
          onPick={(channelId) => {
            // v29. **닫지 않는다** — 새 화면이 열린 채로 마운트하고 그 채널이 나올 때 걷힌다.
            if (channelId === channel.channelId) {
              setChannelSheetOpen(false);
              return;
            }
            onSwitchChannel(channelId, true);
          }}
          onClose={() => {
            setChannelSheetOpen(false);
          }}
          /*
           * 재생 중이지만 **행이 흐르는 근거가 다르다** — ONAIR 행은 절대 시각에서
           * 나오므로 `getPlayhead` 가 필요 없다(`docs/57` §7.1). 개인화 행에 앵커를
           * 잡게 하지 않으려고 **`null` 을 주는 것과 같은 모양**으로 넘긴다.
           */
          now={{ channelId: channel.channelId, getPlayhead: () => null }}
        />
      )}

      {settingsOpen && (
        <SettingsSheet
          init={tv2m.init}
          subscribed={tv2m.pushSubscribed}
          linkedToAccount={tv2m.linkedToAccount}
          runtime={runtime}
          auth={auth}
          ready={tv2m.ready}
          needsIosInstall={tv2m.needsIosInstall}
          lastContext={null}
          /*
           * ★ **둘 다 `null` 이다** (절대 규칙 76). ONAIR 채널에는 광고도 상품도 없으므로
           * 그 줄 자체가 없다 — 없는 것을 끌 수 있게 보이면 그것이 곧 「ONAIR 에도
           * 광고가 있다」는 신호다(규칙 56 이 홈쇼핑에 대해 세운 그 모양).
           *
           * v38 — CSPM 채널에도 그대로 `null` 이다. 그 채널의 광고는 슬레이트 자리라 끄면 슬레이트가 나갈 뿐인데, 그 스위치는
           * `?cspm=off`(진단)이지 시청자의 설정이 아니다(`docs/77` §5.1).
           */
          adPolicy={null}
          offerPolicy={null}
          onOpenLast={() => {
            /* ONAIR 화면에는 「TV에서 본 상품」이 없다 — 구매 표면이 없기 때문이다 */
          }}
          onOpenChannels={
            epgRows.length > 1
              ? () => {
                  setSettingsOpen(false);
                  onEpgOpen();
                  setChannelSheetOpen(true);
                }
              : null
          }
          onEnablePush={tv2m.enablePush}
          onClose={() => {
            setSettingsOpen(false);
          }}
        />
      )}

      <AppHeader />
    </div>
  );
}
